<template>
  <div class="form-list">
    <div class="form-list__toolbar">
      <PageHeader :title="$t('form_list.title')" :description="$t('form_list.description')">
        <template #actions>
          <SplitBtn
            :text="$t('form_list.new_flow')"
            @click="openNewFunnelModal"
            @menu-item-click="handleMenuClick"
            :menu-items="menuItems"
            icon="plus"
            variant="secondary"
            v-if="isAdmin"
          />
        </template>
      </PageHeader>
    </div>
    <EmptyState
      v-if="!forms.length"
      :title="$t('form_list.empty_state.title')"
      :description="$t('form_list.empty_state.description')"
      icon="duotone-folders"
    >
      <div class="empty-state-actions">
        <SplitBtn
          :text="$t('form_list.empty_state.action')"
          @click="openNewFunnelModal"
          @menu-item-click="handleMenuClick"
          :menu-items="menuItems"
          icon="plus"
          variant="secondary"
          :disabled="!isAdmin"
        />
      </div>
    </EmptyState>
    <ul v-else>
      <li v-for="form in forms" :key="form.id">
        <FormCard :form="form" />
      </li>
    </ul>
  </div>
</template>

<script>
  import { computed, onMounted, ref, watch } from 'vue';
  import { useFormStore } from '@/store/formStore';
  import { useUIStore } from '@/store/uiStore';
  import { useI18n } from '@/composables/useI18n';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import FormCard from '@/components/partials/Card/FormCard.vue';
  import SplitBtn from '@/components/partials/SplitBtn.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import { useRoleCheck } from '@/composables/useRoleCheck';
  import CreateFunnelForm from '@/components/dashboard/CreateFunnelForm.vue';
  import ConfiguratorSetupModal from '@/components/products/ConfiguratorSetupModal.vue';

  export default {
    components: {
      FormCard,
      SplitBtn,
      PageHeader,
      EmptyState,
    },
    setup() {
      const { t } = useI18n();
      const formStore = useFormStore();
      const forms = ref(formStore.forms);
      const fetchForms = async () => {
        await formStore.fetchForms();
      };

      const { isAdmin } = useRoleCheck();

      watch(
        () => formStore.forms,
        (newForms) => {
          forms.value = newForms;
        }
      );

      const uiStore = useUIStore();
      const openNewFunnelModal = () => {
        const modalId = uiStore.addModal(CreateFunnelForm, {
          title: t('form_list.modal.title'),
          description: t('form_list.modal.description'),
          onSubmit: async (data) => {
            uiStore.closeModal(modalId);
          },
          onError: (errors) => {
            console.error('Form submission errors:', errors);
          },
        });
      };

      const openNewConfiguratorModal = () => {
        const modalId = uiStore.addModal(ConfiguratorSetupModal, {
          title: t('form_list.configurator_modal.title'),
          description: t('form_list.configurator_modal.description'),
          size: 'xl',
          onSubmit: async (configuratorData) => {
            try {
              await formStore.createForm(configuratorData);
              uiStore.closeModal(modalId);
              // TODO: Redirect to configurator management page instead of form editor
            } catch (error) {
              console.error('Failed to create configurator:', error);
            }
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const menuItems = computed(() => [
        { label: t('form_list.new_configurator'), value: 'configurator' },
      ]);

      const handleMenuClick = (value) => {
        if (value === 'configurator') {
          openNewConfiguratorModal();
        }
      };

      onMounted(fetchForms);

      return {
        openNewFunnelModal,
        openNewConfiguratorModal,
        handleMenuClick,
        menuItems,
        forms,
        isAdmin,
      };
    },
  };
</script>

<style scoped lang="scss">
  .form-list {
    ul {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      gap: 1.5rem;
      list-style: none;
      margin: 0;
      margin-left: 0;
      margin-right: 0;
      li {
        grid-column: span 4 / span 4;
        @include lg-down {
          grid-column: span 12 / span 12;
        }
      }
    }
  }

  .empty-state-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    flex-wrap: wrap;
  }
</style>
